import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { Account, AccountContact } from '@/api/commercial/accounts';
import { accountApi } from '@/api/commercial/accounts';
import type { CreditAnalysis } from '@/api/commercial/credit-analysis';
import type { Proposal } from '@/api/commercial/proposals';
import { proposalApi } from '@/api/commercial/proposals';
import { useToast } from '@/hooks/use-toast';

export interface UpdateProposalData {
  proposal: Proposal | null;
  account: Account | null;
  contacts: AccountContact[];
  selectedContactIds: Set<string>;
  creditAnalysis: CreditAnalysis | null;
}

interface UpdateProposalContextType {
  data: UpdateProposalData;
  isLoading: boolean;
  error: string | null;

  setContacts: (contacts: AccountContact[]) => void;
  setSelectedContactIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  toggleContactSelection: (contactId: string) => void;

  refreshContacts: () => Promise<void>;
  refreshCreditAnalysis: () => Promise<void>;

  updateProposal: (data: Partial<Proposal>) => Promise<void>;
}

const UpdateProposalContext = createContext<UpdateProposalContextType>(
  {} as UpdateProposalContextType,
);

interface UpdateProposalProviderProps {
  children: ReactNode;
  proposalId: string;
}

const initialData: UpdateProposalData = {
  proposal: null,
  account: null,
  contacts: [],
  selectedContactIds: new Set<string>(),
  creditAnalysis: null,
};

function normalizeSelectedContactIds(ids: Iterable<string>): Set<string> {
  const selectedId = Array.from(ids)[0];
  return selectedId ? new Set([selectedId]) : new Set<string>();
}

export function UpdateProposalProvider({ children, proposalId }: UpdateProposalProviderProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [data, setData] = useState<UpdateProposalData>(initialData);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const proposalData = await proposalApi.findOne(proposalId);
      const accountData = await accountApi.findOne(proposalData.clientId);

      let contactsData: AccountContact[] = [];
      if (accountData.id) {
        contactsData = await accountApi.findContacts(accountData.id);
      }

      let creditAnalysisData: CreditAnalysis | null = null;
      try {
        const resolution = await proposalApi.resolveCreditAnalysis(proposalData.id);
        creditAnalysisData = resolution.analysis;
      } catch {
        creditAnalysisData = null;
      }

      const selectedIds = proposalData.contact?.id
        ? new Set([proposalData.contact.id])
        : new Set<string>();

      setData({
        proposal: proposalData,
        account: accountData,
        contacts: contactsData,
        selectedContactIds: selectedIds,
        creditAnalysis: creditAnalysisData,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Erro ao carregar dados da proposta';
      setError(errorMessage);
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [proposalId, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const setSelectedContactIds: React.Dispatch<React.SetStateAction<Set<string>>> = useCallback(
    (action) => {
      setData((prev) => {
        const newIds = typeof action === 'function' ? action(prev.selectedContactIds) : action;
        return { ...prev, selectedContactIds: normalizeSelectedContactIds(newIds) };
      });
    },
    [],
  );

  const toggleContactSelection = useCallback(
    (contactId: string) => {
      setSelectedContactIds((prev) =>
        prev.has(contactId) ? new Set<string>() : new Set([contactId]),
      );
    },
    [setSelectedContactIds],
  );

  const setContacts = useCallback((contacts: AccountContact[]) => {
    setData((prev) => ({ ...prev, contacts }));
  }, []);

  const refreshContacts = useCallback(async () => {
    if (!data.account?.id) return;

    try {
      const contactsData = await accountApi.findContacts(data.account.id);
      setData((prev) => ({ ...prev, contacts: contactsData }));
    } catch (_err) {
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar contatos',
        variant: 'destructive',
      });
    }
  }, [data.account?.id, toast]);

  const refreshCreditAnalysis = useCallback(async () => {
    if (!data.proposal?.id) return;

    try {
      const resolution = await proposalApi.resolveCreditAnalysis(data.proposal.id);
      setData((prev) => ({ ...prev, creditAnalysis: resolution.analysis }));
    } catch (_err) {
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar análise de crédito',
        variant: 'destructive',
      });
    }
  }, [data.proposal?.id, toast]);

  const updateProposal = useCallback(
    async (proposalData: Partial<Proposal>) => {
      if (!data.proposal?.id) return;

      try {
        const updated = await proposalApi.update(data.proposal.id, {
          ...proposalData,
          deliveryMode: proposalData.deliveryMode ?? undefined,
        });
        setData((prev) => ({ ...prev, proposal: updated }));
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['proposal', data.proposal.id] }),
          queryClient.invalidateQueries({
            queryKey: ['proposal-credit-analysis-resolution', data.proposal.id],
          }),
        ]);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar proposta';
        toast({
          title: 'Erro',
          description: errorMessage,
          variant: 'destructive',
        });
        throw err;
      }
    },
    [data.proposal?.id, queryClient, toast],
  );

  const _dataId = useMemo(
    () =>
      `${data.proposal?.id || 'no-proposal'}-${data.account?.id || 'no-account'}-${data.creditAnalysis?.id || 'no-credit'}-${data.contacts.length}-${data.selectedContactIds.size}`,
    [
      data.proposal?.id,
      data.account?.id,
      data.creditAnalysis?.id,
      data.contacts.length,
      data.selectedContactIds.size,
    ],
  );

  const value: UpdateProposalContextType = useMemo(
    () => ({
      data,
      isLoading,
      error,
      setContacts,
      setSelectedContactIds,
      toggleContactSelection,
      refreshContacts,
      refreshCreditAnalysis,
      updateProposal,
    }),
    [
      isLoading,
      error,
      setContacts,
      setSelectedContactIds,
      toggleContactSelection,
      refreshContacts,
      refreshCreditAnalysis,
      updateProposal,
      data,
    ],
  );

  return <UpdateProposalContext.Provider value={value}>{children}</UpdateProposalContext.Provider>;
}

export function useUpdateProposal() {
  const context = useContext(UpdateProposalContext);
  if (!context) {
    throw new Error('useUpdateProposal must be used within an UpdateProposalProvider');
  }
  return context;
}
